import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/finance`;
const TOKEN_KEY = "fin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY));

const http = axios.create({ baseURL: BASE });
http.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d?.msg || e?.message || "حدث خطأ غير متوقع";
};

export const money = (v) =>
  new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v || 0));

export const finApi = {
  // auth
  register: (b) => http.post("/auth/register", b).then((r) => r.data),
  login: (b) => http.post("/auth/login", b).then((r) => r.data),
  me: () => http.get("/auth/me").then((r) => r.data),
  logout: () => http.post("/auth/logout").then((r) => r.data),
  // companies
  companies: () => http.get("/companies").then((r) => r.data),
  createCompany: (b) => http.post("/companies", b).then((r) => r.data),
  updateCompany: (id, b) => http.put(`/companies/${id}`, b).then((r) => r.data),
  switchCompany: (id) => http.post(`/companies/${id}/switch`).then((r) => r.data),
  // branches
  branches: () => http.get("/branches").then((r) => r.data),
  createBranch: (b) => http.post("/branches", b).then((r) => r.data),
  updateBranch: (id, b) => http.put(`/branches/${id}`, b).then((r) => r.data),
  deleteBranch: (id) => http.delete(`/branches/${id}`).then((r) => r.data),
  // cost centers
  costCenters: () => http.get("/cost-centers").then((r) => r.data),
  createCostCenter: (b) => http.post("/cost-centers", b).then((r) => r.data),
  updateCostCenter: (id, b) => http.put(`/cost-centers/${id}`, b).then((r) => r.data),
  deleteCostCenter: (id) => http.delete(`/cost-centers/${id}`).then((r) => r.data),
  // accounts
  openingBalances: () => http.get("/opening-balances").then((r) => r.data),
  saveOpeningBalances: (b) => http.post("/opening-balances", b).then((r) => r.data),
  deleteOpeningBalances: () => http.delete("/opening-balances").then((r) => r.data),
  accounts: (withBal) => http.get(`/accounts${withBal ? "?with_balances=true" : ""}`).then((r) => r.data),
  createAccount: (b) => http.post("/accounts", b).then((r) => r.data),
  updateAccount: (id, b) => http.put(`/accounts/${id}`, b).then((r) => r.data),
  deleteAccount: (id) => http.delete(`/accounts/${id}`).then((r) => r.data),
  seedAccounts: () => http.post("/accounts/seed-default").then((r) => r.data),
  // journals
  journals: (params = {}) => http.get("/journals", { params }).then((r) => r.data),
  journal: (id) => http.get(`/journals/${id}`).then((r) => r.data),
  createJournal: (b) => http.post("/journals", b).then((r) => r.data),
  updateJournal: (id, b) => http.put(`/journals/${id}`, b).then((r) => r.data),
  postJournal: (id) => http.post(`/journals/${id}/post`).then((r) => r.data),
  reverseJournal: (id, reason) => http.post(`/journals/${id}/reverse`, { reason }).then((r) => r.data),
  deleteJournal: (id) => http.delete(`/journals/${id}`).then((r) => r.data),
  // parties
  customers: () => http.get("/customers").then((r) => r.data),
  createCustomer: (b) => http.post("/customers", b).then((r) => r.data),
  updateCustomer: (id, b) => http.put(`/customers/${id}`, b).then((r) => r.data),
  deleteCustomer: (id) => http.delete(`/customers/${id}`).then((r) => r.data),
  suppliers: () => http.get("/suppliers").then((r) => r.data),
  createSupplier: (b) => http.post("/suppliers", b).then((r) => r.data),
  updateSupplier: (id, b) => http.put(`/suppliers/${id}`, b).then((r) => r.data),
  deleteSupplier: (id) => http.delete(`/suppliers/${id}`).then((r) => r.data),
  // reports
  trialBalance: (params = {}) => http.get("/reports/trial-balance", { params }).then((r) => r.data),
  workzoneLink: () => http.get("/workzone-link").then((r) => r.data),
  workzoneBackfill: (from_date) => http.post("/workzone-link/backfill", null, { params: { from_date } }).then((r) => r.data),
  partyStatement: (params) => http.get("/reports/party-statement", { params }).then((r) => r.data),
  partyStatements: (params) => http.get("/reports/party-statements", { params }).then((r) => r.data),
  generalLedger: (params) => http.get("/reports/general-ledger", { params }).then((r) => r.data),
  dashboard: () => http.get("/dashboard").then((r) => r.data),
  auditLogs: () => http.get("/audit-logs").then((r) => r.data),
  // users
  users: () => http.get("/users").then((r) => r.data),
  roles: () => http.get("/users/roles").then((r) => r.data),
  createUser: (b) => http.post("/users", b).then((r) => r.data),
  updateUser: (id, b) => http.put(`/users/${id}`, b).then((r) => r.data),
  // settings
  settings: () => http.get("/settings").then((r) => r.data),
  updateSettings: (b) => http.put("/settings", b).then((r) => r.data),
  // trade documents
  documents: (params = {}) => http.get("/documents", { params }).then((r) => r.data),
  emailDocument: (id, blob) => { const fd = new FormData(); fd.append("file", blob, "doc.pdf"); return http.post(`/documents/${id}/email`, fd).then((r) => r.data); },
  document: (id) => http.get(`/documents/${id}`).then((r) => r.data),
  createDocument: (b) => http.post("/documents", b).then((r) => r.data),
  convertDocument: (id) => http.post(`/documents/${id}/convert`).then((r) => r.data),
  postDocument: (id) => http.post(`/documents/${id}/post`).then((r) => r.data),
  deleteDocument: (id) => http.delete(`/documents/${id}`).then((r) => r.data),
  // payments & banks
  payments: () => http.get("/payments").then((r) => r.data),
  createPayment: (b) => http.post("/payments", b).then((r) => r.data),
  reconcilePayment: (id) => http.post(`/payments/${id}/reconcile`).then((r) => r.data),
  banks: () => http.get("/bank-accounts").then((r) => r.data),
  createBank: (b) => http.post("/bank-accounts", b).then((r) => r.data),
  // zatca
  zatcaInvoices: () => http.get("/zatca/invoices").then((r) => r.data),
  zatcaXmlUrl: (id) => `${BASE}/zatca/invoices/${id}/xml`,
  zatcaQrUrl: (id) => `${BASE}/zatca/invoices/${id}/qr`,
  zatcaQrDataUrl: (id) => http.get(`/zatca/invoices/${id}/qr`, { responseType: "blob" }).then((r) => new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(r.data); })),
  // statements & vat
  vatReturn: (params) => http.get("/reports/vat-return", { params }).then((r) => r.data),
  incomeStatement: (params = {}) => http.get("/reports/income-statement", { params }).then((r) => r.data),
  balanceSheet: (params = {}) => http.get("/reports/balance-sheet", { params }).then((r) => r.data),
  cashFlow: (params) => http.get("/reports/cash-flow", { params }).then((r) => r.data),
  statementOfEquity: (params = {}) => http.get("/reports/statement-of-equity", { params }).then((r) => r.data),
  americanJournal: (params = {}) => http.get("/reports/american-journal", { params }).then((r) => r.data),
  americanJournalExport: (params = {}) => http.get("/reports/american-journal/export", { params, responseType: "blob" }).then((r) => r.data),
  // assets
  assets: () => http.get("/assets").then((r) => r.data),
  assetCategories: () => http.get("/assets/categories").then((r) => r.data),
  createAsset: (b) => http.post("/assets", b).then((r) => r.data),
  updateAsset: (id, b) => http.put(`/assets/${id}`, b).then((r) => r.data),
  deleteAsset: (id) => http.delete(`/assets/${id}`).then((r) => r.data),
  runDepreciation: (b) => http.post("/assets/run-depreciation", b).then((r) => r.data),
  // inventory
  items: () => http.get("/items").then((r) => r.data),
  createItem: (b) => http.post("/items", b).then((r) => r.data),
  warehouses: () => http.get("/warehouses").then((r) => r.data),
  createWarehouse: (b) => http.post("/warehouses", b).then((r) => r.data),
  stockMove: (b) => http.post("/stock/move", b).then((r) => r.data),
  stockMoves: () => http.get("/stock/moves").then((r) => r.data),
  stockLevels: () => http.get("/stock/levels").then((r) => r.data),
  // periods & approvals
  periods: (year) => http.get("/periods", { params: { year } }).then((r) => r.data),
  closePeriod: (key) => http.post("/periods/close", { key }).then((r) => r.data),
  openPeriod: (key) => http.post("/periods/open", { key }).then((r) => r.data),
  closeYear: (year) => http.post("/fiscal-year/close", { year }).then((r) => r.data),
  pendingApprovals: () => http.get("/approvals/pending").then((r) => r.data),
  submitApproval: (entity, id) => http.post("/approvals/submit", { entity, id }).then((r) => r.data),
  approve: (entity, id) => http.post("/approvals/approve", { entity, id }).then((r) => r.data),
  rejectApproval: (entity, id, reason) => http.post("/approvals/reject", { entity, id, reason }).then((r) => r.data),
  // zatca onboarding
  zatcaGenerateCsr: (b = {}) => http.post("/zatca/onboard/generate-csr", b).then((r) => r.data),
  zatcaCompliance: (b) => http.post("/zatca/onboard/compliance", b).then((r) => r.data),
  zatcaComplianceChecks: () => http.post("/zatca/onboard/compliance-checks").then((r) => r.data),
  zatcaProduction: () => http.post("/zatca/onboard/production").then((r) => r.data),
  zatcaAutoSubmit: (v) => http.post("/zatca/onboard/auto-submit", { auto_submit: v }).then((r) => r.data),
  zatcaReset: (b = {}) => http.post("/zatca/onboard/reset", b).then((r) => r.data),
  zatcaPending: () => http.get("/zatca/pending").then((r) => r.data),
  zatcaRetryPending: () => http.post("/zatca/retry-pending").then((r) => r.data),
  zatcaRenew: (b) => http.post("/zatca/onboard/renew", b).then((r) => r.data),
  zatcaSubmit: (id) => http.post(`/zatca/invoices/${id}/submit`).then((r) => r.data),
  zatcaLinkRequest: () => http.get("/zatca/link-request").then((r) => r.data),
  zatcaCreateLinkRequest: (b) => http.post("/zatca/link-request", b).then((r) => r.data),
  zatcaStatus: () => http.get("/zatca/onboard/status").then((r) => r.data),
};
